/**
 * Template Builder Page — first-class section builder.
 *
 * Sections are first-class: each has an editable title + description and holds a
 * list of items (questions). A section may optionally be bound to an Area + Part
 * instance (the part's MachinePartType supplies selectable measurement keys).
 * Supports optional machine assignment, auto-save drafts, publish validation,
 * and a version-history sidebar.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import BuildIcon from '@mui/icons-material/Build';
import ListAltIcon from '@mui/icons-material/ListAlt';
import TemplatePreviewDialog from './TemplatePreviewDialog';
import SidebarTracker from './SidebarTracker';

import {
  ChecklistTemplateControllerApi,
  MachineControllerApi,
  MachineChecklistCategoryControllerApi,
  SafetyWarningControllerApi,
  MachinePartTypeControllerApi,
  TemplateVersionsApi,
} from '../../api-generated/apis';
import type {
  ChecklistTemplate,
  Machine,
  MachinePart,
  MachineChecklistCategory,
  Area,
  MachinePartType,
  MeasurementSpec,
} from '../../api-generated';
import { apiConfig } from '../../api-generated/apiConfig';
import type { SectionType } from '../../types';
import { useTemplateVersions } from '../../hooks/useTemplateVersions';

const templateApi = new ChecklistTemplateControllerApi(apiConfig);
const versionsApi = new TemplateVersionsApi(apiConfig);
const machineApi = new MachineControllerApi(apiConfig);
const categoryApi = new MachineChecklistCategoryControllerApi(apiConfig);
const partTypeApi = new MachinePartTypeControllerApi(apiConfig);

// ─── Types ───────────────────────────────────────────────────────────────────

interface Item {
  id?: string;
  question: string;
  description?: string;
  machinePartId?: string;
  machinePartName?: string;
  measurementKey?: string;
  sectionType: SectionType;
  minValue?: number;
  maxValue?: number;
  warningMin?: number;
  warningMax?: number;
}

/** First-class section: editable title/description, optional Area binding, list of items.
 *  Part binding lives on each item (question), not the section. */
interface Section {
  id?: string;
  title: string;
  description?: string;
  areaId?: string;
  areaName?: string;
  items: Item[];
}

interface Option {
  id: string;
  name: string;
}

const SECTION_TYPE_OPTIONS: { value: SectionType; label: string }[] = [
  { value: 'STATUS_CHECK', label: 'Condition Check' },
  { value: 'NUMBER_ENTRY', label: 'Number Entry' },
  { value: 'TEXT_RESULT', label: 'Text Result' },
  { value: 'MULTI_VALUE', label: 'Multiple Readings' },
];


const newItem = (): Item => ({ question: '', sectionType: 'STATUS_CHECK' });

// ─── Component ───────────────────────────────────────────────────────────────

const TemplateBuilderPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [templateName, setTemplateName] = useState('Untitled Template');
  const [description, setDescription] = useState('');
  const [machineId, setMachineId] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [generalWarningIds, setGeneralWarningIds] = useState<string[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [isDirty, setIsDirty] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [publishing, setPublishing] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [confirmPublishOpen, setConfirmPublishOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // ─── Queries ────────────────────────────────────────────────────────────

  const { data: templateData, isLoading } = useQuery<ChecklistTemplate>({
    queryKey: ['templates', 'detail', id],
    queryFn: () => templateApi.getTemplate({ id: id! }),
    enabled: !!id && id !== 'new',
  });

  const { data: machinesData } = useQuery<Machine[]>({
    queryKey: ['machines-list'],
    queryFn: () => machineApi.getAllMachines(),
    staleTime: 60_000,
  });

  const { data: categoriesData } = useQuery<MachineChecklistCategory[]>({
    queryKey: ['categories-list'],
    queryFn: () => categoryApi.getAllCategories(),
    staleTime: 60_000,
  });

  const { data: warningsData } = useQuery<any[]>({
    queryKey: ['safety-warnings-list'],
    queryFn: async () => new SafetyWarningControllerApi(apiConfig).getAll1(),
    staleTime: 60_000,
  });

  // Version lifecycle: content lives on the version (draft/publish/history), not the template.
  const templateIdForVersions = id && id !== 'new' ? id : undefined;
  const {
    versionHistory,
    draft,
    activeVersion,
    isSaving,
  } = useTemplateVersions(templateIdForVersions);

  // Areas for the assigned machine (for section binding)
  const { data: areasData } = useQuery<Area[]>({
    queryKey: ['areas', machineId],
    queryFn: () => machineApi.getAreas({ id: machineId! }),
    enabled: !!machineId,
  });

  // All part instances for the machine, resolved from the separate MACHINE_PART rows via the
  // per-area endpoint. (The legacy /machine/{id}/parts endpoint returns machine.getParts(), which
  // is the embedded list and is empty when parts are stored as their own rows — as the seed does.)
  const areaIdsForParts = useMemo(
    () => (Array.isArray(areasData) ? areasData.map((a) => a.id).filter(Boolean) : []) as string[],
    [areasData],
  );
  const { data: partsData } = useQuery<MachinePart[]>({
    queryKey: ['machine-parts', machineId, areaIdsForParts],
    queryFn: async () => {
      if (!machineId || areaIdsForParts.length === 0) return [];
      const perArea = await Promise.all(
        areaIdsForParts.map((areaId) =>
          machineApi
            .getPartsInArea({ id: machineId, areaId })
            .then((parts) => (Array.isArray(parts) ? parts : []))
            .catch(() => [] as MachinePart[]),
        ),
      );
      return perArea.flat();
    },
    enabled: !!machineId && areaIdsForParts.length > 0,
  });

  // Part types (kinds) — supply measurement keys for a bound part
  const { data: partTypesData } = useQuery<MachinePartType[]>({
    queryKey: ['machine-part-types'],
    queryFn: () => partTypeApi.getAllMachinePartTypes(),
    staleTime: 60_000,
  });

  const machineOptions: Option[] = useMemo(
    () => (machinesData ?? []).map((m: any) => ({ id: m.id ?? '', name: m.name ?? '' })),
    [machinesData],
  );
  const categoryOptions = useMemo(
    () => (categoriesData ?? []).map((c: any) => ({ id: c.id ?? '', name: c.name ?? '' })),
    [categoriesData],
  );
  const warningOptions = useMemo(
    () => (warningsData ?? []).map((w: any) => ({ id: w.id ?? '', title: w.title ?? '' })),
    [warningsData],
  );
  const areaOptions: Option[] = useMemo(
    () => (areasData ?? []).map((a: Area) => ({ id: a.id ?? '', name: a.name ?? '' })),
    [areasData],
  );

  const partsByArea = useMemo(() => {
    const map: Record<string, MachinePart[]> = {};
    (partsData ?? []).forEach((p: MachinePart) => {
      const aid = (p as any).areaId ?? '__unassigned';
      (map[aid] ??= []).push(p);
    });
    return map;
  }, [partsData]);

  /** Measurement keys available for a part (via its MachinePartType). */
  const measurementKeysForPart = useCallback(
    (machinePartId?: string): { key: string; label: string }[] => {
      if (!machinePartId) return [];
      const part = (partsData ?? []).find((p: MachinePart) => p.id === machinePartId);
      const typeId = (part as any)?.machinePartTypeId;
      if (!typeId) return [];
      const pt = (partTypesData ?? []).find((t: MachinePartType) => t.id === typeId);
      return (pt?.measurementSpecs ?? []).map((s: MeasurementSpec) => ({ key: s.key ?? '', label: s.label || s.key || '' }));
    },
    [partsData, partTypesData],
  );

  // ─── Seed state from fetched template ───────────────────────────────────

  useEffect(() => {
    if (!templateData) return;
    setTemplateName(templateData.name ?? '');
    setDescription(templateData.description ?? '');
    setMachineId((templateData as any).machineId ?? null);
    setCategoryId((templateData as any).category?.id ?? null);
    setGeneralWarningIds((templateData as any).generalWarningIds ?? []);
  }, [templateData]);

  // Seed sections from the DRAFT version (content lives on the version, nested section→items).
  // Seed sections from the DRAFT version if one exists, otherwise from the ACTIVE (published)
  // version. A freshly published template has no draft until it's edited, so we must fall back to
  // the active version's content — otherwise the builder would show no sections.
  useEffect(() => {
    const source: any = draft ?? activeVersion;
    if (!source) return;
    const draftSections = Array.isArray(source.sections) ? source.sections : [];
    const seeded: Section[] = draftSections.map((s: any) => ({
      id: s.id,
      title: s.title ?? 'Section',
      description: s.description ?? undefined,
      areaId: s.areaId ?? undefined,
      areaName: s.areaName ?? undefined,
      items: (Array.isArray(s.items) ? s.items : []).map((it: any) => ({
        id: it.id,
        question: it.question ?? '',
        description: it.description ?? undefined,
        machinePartId: it.machinePartId ?? undefined,
        measurementKey: it.measurementKey ?? undefined,
        sectionType: (it.sectionType ?? 'STATUS_CHECK') as SectionType,
        minValue: it.thresholds?.criticalMin,
        maxValue: it.thresholds?.criticalMax,
        warningMin: it.thresholds?.warningMin,
        warningMax: it.thresholds?.warningMax,
      })),
    }));
    setSections(seeded);
  }, [draft, activeVersion]);

  // ─── Payload ────────────────────────────────────────────────────────────

  /** Template metadata only — content lives on the version, not the template. */
  const buildMetadataPayload = () => {
    const selectedCategory = categoryOptions.find((c: any) => c.id === categoryId);
    return {
      name: templateName,
      description,
      machineId: machineId || undefined,
      generalWarningIds: generalWarningIds.length > 0 ? generalWarningIds : undefined,
      category: selectedCategory ? { id: selectedCategory.id, name: selectedCategory.name } : undefined,
    };
  };

  /** Nested version sections (section → items) matching the backend TemplateSection/SectionItem. */
  const buildVersionSections = () =>
    sections.map((s, si) => ({
      id: s.id,
      title: s.title || `Section ${si + 1}`,
      description: s.description || undefined,
      areaId: s.areaId || undefined,
      areaName: s.areaName || undefined,
      order: si,
      items: s.items.map((it, ii) => ({
        id: it.id,
        question: it.question,
        description: it.description || undefined,
        sectionType: it.sectionType,
        required: true,
        order: ii,
        machinePartId: it.machinePartId || undefined,
        measurementKey: it.measurementKey || undefined,
        thresholds:
          it.warningMin != null ||
          it.warningMax != null ||
          it.minValue != null ||
          it.maxValue != null
            ? {
                warningMin: it.warningMin,
                warningMax: it.warningMax,
                criticalMin: it.minValue,
                criticalMax: it.maxValue,
              }
            : undefined,
      })),
    }));

  // ─── Save-status auto-clear ───────────────────────────────────────────────

  useEffect(() => {
    if (saveStatus === 'saved') {
      const t = setTimeout(() => setSaveStatus('idle'), 3000);
      return () => clearTimeout(t);
    }
  }, [saveStatus]);

  // ─── Handlers ───────────────────────────────────────────────────────────

  const markDirty = useCallback(() => { setIsDirty(true); setSaveStatus('idle'); }, []);

  const handleSave = useCallback(async () => {
    setPublishing(true);
    try {
      let templateId = id && id !== 'new' ? id : undefined;
      if (!templateId) {
        const created = await templateApi.createTemplate({ checklistTemplate: buildMetadataPayload() as any });
        templateId = created.id!;
      } else {
        await templateApi.updateTemplate({ id: templateId, checklistTemplate: buildMetadataPayload() as any });
      }
      await versionsApi.saveDraft({
        templateId,
        saveDraftRequest: { sections: buildVersionSections() as any } as any,
      });
      setIsDirty(false);
      setSaveStatus('saved');
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['templateVersions', templateId] });
      if (!id || id === 'new') navigate(`/templates/${templateId}/edit`, { replace: true });
    } catch (e: any) {
      setErrors([`Failed to save draft: ${e?.message ?? 'Unknown error'}`]);
      setSaveStatus('idle');
    } finally {
      setPublishing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, sections, templateName, description, machineId, categoryId, generalWarningIds, navigate, queryClient]);

  // Validate, then ask the user to confirm before going live.
  const handlePublish = useCallback(() => {
    if (sections.length === 0) { setErrors(['Template must have at least one section.']); return; }
    const emptyTitle = sections.some((s) => !s.title.trim());
    if (emptyTitle) { setErrors(['Every section must have a title.']); return; }
    const emptyItem = sections.some((s) => s.items.length === 0 || s.items.some((it) => !it.question.trim()));
    if (emptyItem) { setErrors(['Every section must have at least one question, and all questions need text.']); return; }
    setErrors([]);
    setConfirmPublishOpen(true);
  }, [sections]);

  const doPublish = useCallback(async () => {
    setConfirmPublishOpen(false);
    setErrors([]);
    setPublishing(true);
    try {
      // One-go publish: create the template (auto-creates a V1 draft) if new, then write the
      // current content to the draft and publish it — no separate "Save Draft" click required.
      let templateId = id && id !== 'new' ? id : undefined;
      if (!templateId) {
        const created = await templateApi.createTemplate({ checklistTemplate: buildMetadataPayload() as any });
        templateId = created.id!;
      } else {
        await templateApi.updateTemplate({ id: templateId, checklistTemplate: buildMetadataPayload() as any });
      }
      // Save the current sections into the draft, then publish it.
      await versionsApi.saveDraft({
        templateId,
        saveDraftRequest: { sections: buildVersionSections() as any } as any,
      });
      await versionsApi.publishDraft({
        templateId,
        publishDraftRequest: { changelog: 'Published from template builder' },
      });
      setIsDirty(false);
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['templateVersions', templateId] });
      queryClient.invalidateQueries({ queryKey: ['templates', 'detail', templateId] });
      setPublishSuccess(true);
      if (!id || id === 'new') navigate(`/templates/${templateId}/edit`, { replace: true });
    } catch (e: any) {
      setErrors([`Failed to publish: ${e?.message ?? 'Unknown error'}`]);
    } finally {
      setPublishing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, sections, templateName, description, machineId, categoryId, generalWarningIds, navigate, queryClient]);

  const addSection = useCallback((opts?: { title?: string; areaId?: string; areaName?: string }) => {
    setSections((prev) => [
      ...prev,
      {
        title: opts?.title ?? 'New Section',
        areaId: opts?.areaId,
        areaName: opts?.areaName,
        items: [newItem()],
      },
    ]);
    markDirty();
    setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }), 100);
  }, [markDirty]);

  const updateSection = useCallback((si: number, patch: Partial<Section>) => {
    setSections((prev) => prev.map((s, i) => (i === si ? { ...s, ...patch } : s)));
    markDirty();
  }, [markDirty]);

  const deleteSection = useCallback((si: number) => {
    setSections((prev) => prev.filter((_, i) => i !== si));
    markDirty();
  }, [markDirty]);

  const addItem = useCallback((si: number) => {
    setSections((prev) => prev.map((s, i) => (i === si ? { ...s, items: [...s.items, newItem()] } : s)));
    markDirty();
  }, [markDirty]);

  const updateItem = useCallback((si: number, ii: number, patch: Partial<Item>) => {
    setSections((prev) => prev.map((s, i) =>
      i === si ? { ...s, items: s.items.map((it, j) => (j === ii ? { ...it, ...patch } : it)) } : s,
    ));
    markDirty();
  }, [markDirty]);

  const deleteItem = useCallback((si: number, ii: number) => {
    setSections((prev) => prev.map((s, i) =>
      i === si ? { ...s, items: s.items.filter((_, j) => j !== ii) } : s,
    ));
    markDirty();
  }, [markDirty]);

  // ─── Render helpers ──────────────────────────────────────────────────────

  const renderItem = (section: Section, si: number, item: Item, ii: number) => {
    const measKeys = measurementKeysForPart(item.machinePartId);
    const areaParts = section.areaId ? (partsByArea[section.areaId] ?? []) : [];
    return (
      <Paper key={ii} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Box sx={{ flex: 1 }}>
            <TextField label="Question" value={item.question} size="small" fullWidth sx={{ mb: 1 }}
              onChange={(e) => updateItem(si, ii, { question: e.target.value })} inputProps={{ 'data-testid': `tpl-question-${si}-${ii}` }} />
            <TextField label="Description (optional)" value={item.description ?? ''} size="small" fullWidth sx={{ mb: 1 }}
              onChange={(e) => updateItem(si, ii, { description: e.target.value })} />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <TextField label="Type" select size="small" value={item.sectionType} sx={{ minWidth: 160 }}
                onChange={(e) => updateItem(si, ii, { sectionType: e.target.value as SectionType })}>
                {SECTION_TYPE_OPTIONS.map((opt) => (<MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>))}
              </TextField>
              {section.areaId && (
                <TextField label="Part (optional)" select size="small" value={item.machinePartId ?? ''} sx={{ minWidth: 180 }}
                  onChange={(e) => {
                    const pid = e.target.value || undefined;
                    const part = areaParts.find((p: MachinePart) => p.id === pid);
                    // changing part clears a measurement key that no longer applies
                    updateItem(si, ii, { machinePartId: pid, machinePartName: part?.name ?? undefined, measurementKey: undefined });
                  }}
                  helperText="Area, or a specific part">
                  <MenuItem value="">— Area (no specific part) —</MenuItem>
                  {areaParts.map((p: MachinePart) => (<MenuItem key={p.id} value={p.id ?? ''}>{p.name}</MenuItem>))}
                </TextField>
              )}
              {measKeys.length > 0 && (
                <TextField label="Measurement" select size="small" value={item.measurementKey ?? ''} sx={{ minWidth: 160 }}
                  onChange={(e) => updateItem(si, ii, { measurementKey: e.target.value || undefined })}>
                  <MenuItem value="">— None —</MenuItem>
                  {measKeys.map((m) => (<MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>))}
                </TextField>
              )}
            </Stack>
            {(item.sectionType === 'NUMBER_ENTRY' || item.sectionType === 'MULTI_VALUE') && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
                <TextField label="Min" type="number" size="small" value={item.minValue ?? ''}
                  onChange={(e) => updateItem(si, ii, { minValue: e.target.value === '' ? undefined : Number(e.target.value) })} />
                <TextField label="Max" type="number" size="small" value={item.maxValue ?? ''}
                  onChange={(e) => updateItem(si, ii, { maxValue: e.target.value === '' ? undefined : Number(e.target.value) })} />
                <TextField label="Warning Min" type="number" size="small" value={item.warningMin ?? ''}
                  onChange={(e) => updateItem(si, ii, { warningMin: e.target.value === '' ? undefined : Number(e.target.value) })} />
                <TextField label="Warning Max" type="number" size="small" value={item.warningMax ?? ''}
                  onChange={(e) => updateItem(si, ii, { warningMax: e.target.value === '' ? undefined : Number(e.target.value) })} />
              </Box>
            )}
          </Box>
          <IconButton size="small" color="error" onClick={() => deleteItem(si, ii)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Paper>
    );
  };

  const renderSection = (section: Section, si: number) => (
    <Card id={`section-card-${si}`} data-testid={`tpl-section-${si}`} key={si} variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
        <Box sx={{ flex: 1 }}>
          <TextField label="Section Title" value={section.title} size="small" fullWidth sx={{ mb: 1 }}
            onChange={(e) => updateSection(si, { title: e.target.value })} inputProps={{ 'data-testid': `tpl-section-title-${si}` }} />
          <TextField label="Section Description (optional)" value={section.description ?? ''} size="small" fullWidth
            onChange={(e) => updateSection(si, { description: e.target.value })} />
        </Box>
        <IconButton size="small" color="error" onClick={() => deleteSection(si)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Stack>

      {/* Optional Area binding (Part is chosen per-question below) */}
      {machineId && (
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap alignItems="center">
          <TextField
            label="Area (optional)"
            select size="small" sx={{ minWidth: 240 }}
            value={section.areaId ?? ''}
            onChange={(e) => {
              const areaId = e.target.value || undefined;
              const area = (areasData ?? []).find((a: Area) => a.id === areaId);
              // Area changed → clear any per-item part bindings that no longer apply
              const clearedItems = section.items.map((it) => ({
                ...it,
                machinePartId: undefined,
                machinePartName: undefined,
                measurementKey: undefined,
              }));
              updateSection(si, { areaId, areaName: area?.name ?? undefined, items: clearedItems });
            }}
            helperText="Bind this section to a physical area; pick specific parts per question"
          >
            <MenuItem value="">— None (general section) —</MenuItem>
            {areaOptions.map((area) => (
              <MenuItem key={area.id} value={area.id}>{area.name}</MenuItem>
            ))}
          </TextField>
          {section.areaName && <Chip label={section.areaName} color="primary" variant="outlined" size="small" />}
        </Stack>
      )}

      <Divider sx={{ mb: 1.5 }} />
      {section.items.map((it, ii) => renderItem(section, si, it, ii))}
      <Button size="small" startIcon={<AddIcon />} onClick={() => addItem(si)} data-testid={`tpl-add-question-${si}`}>Add Question</Button>
    </Card>
  );

  // ─── Loading ───────────────────────────────────────────────────────────

  if (isLoading) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">Loading...</Typography></Box>;
  }

  const versions = (Array.isArray(versionHistory) ? versionHistory : []).map((v: any) => ({
    id: v.id, version: v.versionNumber ?? 0, status: v.status ?? 'DRAFT',
    createdAt: v.createdAt?.toISOString?.() ?? v.createdAt ?? '',
  }));

  const handleScrollToSection = (index: number) => {
    const el = document.getElementById(`section-card-${index}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Flatten sections→items for the preview + sidebar tracker
  const flatForPreview = sections.flatMap((s) =>
    s.items.map((it) => ({
      question: it.question,
      sectionType: it.sectionType,
      machinePartName: it.machinePartName ?? s.areaName,
      minValue: it.minValue, maxValue: it.maxValue, warningMin: it.warningMin, warningMax: it.warningMax,
    })),
  );

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', gap: 2, py: 2 }}>
      <Box sx={{ flex: 1, maxWidth: 900 }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <TextField value={templateName} onChange={(e) => { setTemplateName(e.target.value); markDirty(); }}
            variant="standard" placeholder="Template Name"
            InputProps={{ disableUnderline: true, sx: { fontSize: '1.5rem', fontWeight: 600 } }} sx={{ flex: 1, mr: 2 }}
            inputProps={{ 'data-testid': 'tpl-name' }} />
          <Stack direction="row" spacing={1} alignItems="center">
            {saveStatus === 'saving' && <Typography variant="body2" color="text.secondary">Saving...</Typography>}
            {saveStatus === 'saved' && <Typography variant="body2" color="success.main">Saved</Typography>}
            <Button variant="text" size="small" startIcon={<VisibilityIcon />} onClick={() => setPreviewOpen(true)} disabled={sections.length === 0}>Preview</Button>
            <Button variant="outlined" size="small" onClick={handleSave} disabled={publishing || isSaving} data-testid="tpl-save-draft">Save Draft</Button>
            <Button variant="contained" size="small" onClick={handlePublish} disabled={publishing} data-testid="tpl-publish">Publish</Button>
          </Stack>
        </Stack>

        {errors.length > 0 && <Alert severity="error" sx={{ mb: 2 }}>{errors.join('. ')}</Alert>}
        {publishSuccess && <Alert severity="success" sx={{ mb: 2 }}>Published successfully.</Alert>}

        {/* Category & Machine selector */}
        <Card variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Category (optional)</Typography>
              <Autocomplete options={categoryOptions} getOptionLabel={(opt) => opt.name}
                value={categoryOptions.find((c: any) => c.id === categoryId) ?? null}
                onChange={(_, val) => { setCategoryId(val?.id ?? null); markDirty(); }}
                renderInput={(params) => <TextField {...params} placeholder="Select category..." size="small" />}
                isOptionEqualToValue={(opt, val) => opt.id === val.id} />
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Assign to Machine (optional)</Typography>
              <Autocomplete options={machineOptions} getOptionLabel={(opt) => opt.name}
                value={machineOptions.find((m) => m.id === machineId) ?? null}
                onChange={(_, val) => { setMachineId(val?.id ?? null); markDirty(); }}
                renderInput={(params) => <TextField {...params} placeholder="Select machine..." size="small" />}
                isOptionEqualToValue={(opt, val) => opt.id === val.id} />
              {machineId && <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Sections can be bound to this machine&apos;s areas; each question can target a specific part.</Typography>}
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>General Safety Warnings (optional)</Typography>
              <Autocomplete multiple options={warningOptions} getOptionLabel={(opt: any) => opt.title}
                value={warningOptions.filter((w: any) => generalWarningIds.includes(w.id))}
                onChange={(_, val) => { setGeneralWarningIds(val.map((v: any) => v.id)); markDirty(); }}
                renderInput={(params) => <TextField {...params} placeholder="Select warnings..." size="small" />}
                isOptionEqualToValue={(opt: any, val: any) => opt.id === val.id} />
            </Box>
          </Stack>
        </Card>

        <Divider sx={{ mb: 3 }} />

        {/* Sections */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography variant="h6">Sections</Typography>
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => addSection()} data-testid="tpl-add-section">Add Section</Button>
        </Stack>
        {sections.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            No sections yet. Add a custom section, or use the button (bottom-right) to add an area section.
          </Typography>
        )}
        {sections.map((s, si) => renderSection(s, si))}
      </Box>

      {/* Sidebar tracker */}
      <SidebarTracker
        sections={sections.map((s, idx) => ({ index: idx, question: s.title, partName: s.areaName }))}
        versions={versions}
        onSectionClick={handleScrollToSection}
      />

      {/* Preview Dialog */}
      {/* Publish confirmation — publishing goes live and locks this version */}
      <Dialog open={confirmPublishOpen} onClose={() => setConfirmPublishOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Publish this version?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            Publishing makes this version <strong>live</strong> — it becomes the version used to
            generate inspection forms.
            <ul style={{ marginTop: 8, marginBottom: 8 }}>
              <li>A published version is <strong>locked</strong> and can no longer be edited.</li>
              <li>To change it later, you create a <strong>new version</strong> (edit → save as draft or publish).</li>
              <li>When the new version is published, the current live version is <strong>deactivated</strong>.</li>
            </ul>
            Do you want to publish now?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPublishOpen(false)} disabled={publishing}>Cancel</Button>
          <Button variant="contained" onClick={doPublish} disabled={publishing} data-testid="tpl-publish-confirm">
            Publish &amp; Go Live
          </Button>
        </DialogActions>
      </Dialog>

      <TemplatePreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        templateName={templateName}
        machineName={machineOptions.find((m) => m.id === machineId)?.name}
        sections={flatForPreview}
      />

      {/* Floating Add: custom section + one area-bound section per area */}
      <SpeedDial ariaLabel="Add section" sx={{ position: 'fixed', bottom: 24, right: 24 }} icon={<SpeedDialIcon />}>
        <SpeedDialAction key="custom" icon={<ListAltIcon />} tooltipTitle="Custom Section" onClick={() => addSection()} />
        {machineId && areaOptions.map((area) => (
          <SpeedDialAction
            key={area.id}
            icon={<BuildIcon />}
            tooltipTitle={`Area: ${area.name}`}
            onClick={() => addSection({ title: area.name, areaId: area.id, areaName: area.name })}
          />
        ))}
      </SpeedDial>
    </Box>
  );
};

export default TemplateBuilderPage;
